/**
 * MedicalSection — Life › Health › Vitals › Doctors & Records.
 * Appointments, prescriptions and doctor notes, persisted in nexa_medical.
 */
import { useEffect, useState } from 'react';
import { Stethoscope, Pill, FileText, CalendarClock, Plus, Trash2, Paperclip } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { toast } from 'sonner';
import { nexaApi } from './api';

const KINDS = [
  ['appointment', 'Appointment', CalendarClock, 'text-sky-300 bg-sky-400/10 border-sky-400/25'],
  ['prescription', 'Prescription', Pill, 'text-emerald-300 bg-emerald-400/10 border-emerald-400/25'],
  ['note', 'Doctor note', FileText, 'text-violet-300 bg-violet-400/10 border-violet-400/25'],
];
const meta = (k) => KINDS.find((x) => x[0] === k) || KINDS[2];
const fmtDay = (iso) => {
  if (!iso) return '';
  try {
    return new Date(iso + 'T00:00:00').toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
  } catch { return iso; }
};

const emptyForm = { kind: 'appointment', title: '', doctor: '', date: '', details: '' };

export const MedicalSection = () => {
  const [records, setRecords] = useState(null);
  const [showAdd, setShowAdd] = useState(false);
  const [f, setF] = useState(emptyForm);
  const [file, setFile] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = () => nexaApi.get('/health/medical')
    .then(({ data }) => setRecords(data.records || []))
    .catch(() => setRecords([]));
  useEffect(() => { load(); }, []);

  const toB64 = (fl) => new Promise((res, rej) => {
    const r = new FileReader(); r.onload = () => res(r.result); r.onerror = rej; r.readAsDataURL(fl);
  });

  const add = async () => {
    if (!f.title.trim()) { toast.error('Add a title'); return; }
    setSaving(true);
    try {
      const { data: r } = await nexaApi.post('/health/medical', f);
      if (file && r.record?.id) {
        const b64 = await toB64(file);
        await nexaApi.post(`/health/medical/${r.record.id}/attachment`,
          { filename: file.name, content_type: file.type, data_base64: b64 });
      }
      toast.success('Saved');
      setF(emptyForm); setFile(null); setShowAdd(false); load();
    } catch { toast.error('Could not save'); } finally { setSaving(false); }
  };
  const del = async (id) => {
    if (!window.confirm('Delete this record?')) return;
    setRecords((prev) => (prev || []).filter((r) => r.id !== id));
    try { await nexaApi.delete(`/health/medical/${id}`); } catch { toast.error('Could not delete'); load(); }
  };
  const openAttachment = async (r) => {
    try {
      const resp = await nexaApi.get(`/health/medical/${r.id}/attachment`, { responseType: 'blob' });
      window.open(URL.createObjectURL(resp.data), '_blank');
    } catch { toast.error('Could not open file'); }
  };

  const inp = 'w-full bg-white/[0.04] border border-white/10 rounded-xl px-3 py-2 text-[13px] text-white/85 outline-none focus:border-sky-400/40';

  return (
    <section data-testid="nexa-medical-section">
      <div className="flex items-center justify-between mb-3">
        <span className="flex items-center gap-2 text-[11px] tracking-[0.25em] uppercase text-sky-400/70 font-mono">
          <Stethoscope className="w-3.5 h-3.5" /> Doctors &amp; records
        </span>
        <button onClick={() => setShowAdd((v) => !v)} data-testid="nexa-medical-add-toggle"
          className="flex items-center gap-1 text-[11px] text-sky-300 hover:text-sky-200 active:scale-95">
          <Plus className="w-3.5 h-3.5" /> Add
        </button>
      </div>
      <p className="text-[11px] text-white/35 -mt-1 mb-3">NEXA reminds you the day before an appointment and when a prescription is due for a refill.</p>

      <AnimatePresence initial={false}>
        {showAdd && (
          <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: 'auto', opacity: 1 }} exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.22 }} className="overflow-hidden mb-4">
            <div className="rounded-2xl border border-white/10 bg-[#0A0A0A] p-4 flex flex-col gap-2.5" data-testid="nexa-medical-form">
              <div className="grid grid-cols-3 gap-1.5">
                {KINDS.map(([v, l]) => (
                  <button key={v} onClick={() => setF({ ...f, kind: v })} data-testid={`nexa-medical-kind-${v}`}
                    className={`h-9 rounded-xl text-[12px] font-medium transition-colors ${f.kind === v ? 'bg-sky-400 text-black' : 'text-white/55 bg-white/[0.04] border border-white/10'}`}>{l}</button>
                ))}
              </div>
              <input value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} placeholder="Title (e.g. Cardiology check-up)" className={inp} data-testid="nexa-medical-title" />
              <div className="grid grid-cols-2 gap-2">
                <input value={f.doctor} onChange={(e) => setF({ ...f, doctor: e.target.value })} placeholder="Doctor / clinic" className={inp} data-testid="nexa-medical-doctor" />
                <input type="date" value={f.date} onChange={(e) => setF({ ...f, date: e.target.value })} className={inp} data-testid="nexa-medical-date" />
              </div>
              <textarea value={f.details} onChange={(e) => setF({ ...f, details: e.target.value })} placeholder="Details, dosage, results, next steps…" rows={2} className={inp} data-testid="nexa-medical-details" />
              <label className="flex items-center gap-2 text-[12px] text-white/60 cursor-pointer">
                <Paperclip className="w-3.5 h-3.5 text-sky-400" />
                <span className="truncate">{file ? file.name : 'Attach a lab result or referral (photo / PDF)'}</span>
                <input type="file" accept="image/*,application/pdf" className="hidden" data-testid="nexa-medical-file"
                  onChange={(e) => setFile(e.target.files?.[0] || null)} />
              </label>
              <div className="flex gap-2 mt-0.5">
                <button onClick={() => { setShowAdd(false); setF(emptyForm); setFile(null); }} className="flex-1 h-9 rounded-xl border border-white/12 text-[12.5px] text-white/70">Cancel</button>
                <button onClick={add} disabled={saving} data-testid="nexa-medical-save" className="flex-1 h-9 rounded-xl bg-sky-400 text-black font-semibold text-[12.5px] active:scale-[0.98] disabled:opacity-50">{saving ? 'Saving…' : 'Save'}</button>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <div className="rounded-3xl border border-white/10 bg-[#0A0A0A] overflow-hidden" data-testid="nexa-medical-list">
        {records === null && <div className="px-5 py-6 text-sm text-white/40">Loading…</div>}
        {records !== null && records.length === 0 && (
          <div className="px-5 py-6 text-sm text-white/50">No records yet. Add an appointment, prescription or doctor note.</div>
        )}
        {(records || []).map((r, idx) => {
          const [, label, Icon, cls] = meta(r.kind);
          return (
            <div key={r.id} data-testid={`nexa-medical-${r.id}`}
              className={`px-5 py-4 flex items-start gap-3.5 ${idx < records.length - 1 ? 'border-b border-white/[0.06]' : ''}`}>
              <div className={`shrink-0 w-10 h-10 rounded-xl border flex items-center justify-center ${cls}`}>
                <Icon className="w-5 h-5" strokeWidth={1.6} />
              </div>
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-[13px] text-white/90 font-medium">{r.title}</span>
                  <span className={`text-[9px] px-1.5 py-0.5 rounded-full border ${cls}`}>{label}</span>
                </div>
                <div className="text-[11.5px] text-white/45 mt-0.5">
                  {[r.doctor, fmtDay(r.date)].filter(Boolean).join(' · ') || 'no date'}
                </div>
                {r.details && <p className="text-[12px] text-white/60 mt-1 leading-relaxed whitespace-pre-wrap">{r.details}</p>}
                {r.attachment_name && (
                  <button onClick={() => openAttachment(r)} data-testid={`nexa-medical-file-${r.id}`}
                    className="mt-2 inline-flex items-center gap-1.5 text-[11.5px] text-sky-300 hover:text-sky-200">
                    <Paperclip className="w-3.5 h-3.5" /> {r.attachment_name}
                  </button>
                )}
              </div>
              <button onClick={() => del(r.id)} data-testid={`nexa-medical-del-${r.id}`}
                className="shrink-0 w-8 h-8 rounded-full text-white/25 hover:text-rose-300 active:scale-90 flex items-center justify-center">
                <Trash2 className="w-3.5 h-3.5" />
              </button>
            </div>
          );
        })}
      </div>
    </section>
  );
};

export default MedicalSection;
